// Formula Analyzer — Office.js writer. Creates a new worksheet and paints
// a step-by-step "explanation ladder" for a formula. Isolated feature
// file: safe to delete for a full revert.
//
// Office.js discipline (mirrors services/office.ts): batch every write on
// the request context and sync ONCE at the end; the only earlier sync is
// the sheet-name collision check, which we need resolved before add().

import { isInOffice } from './office';
import type { FormulaExplanation } from './formulaAnalyzer';

const SHEET_BASE_NAME = 'Formula Explained';

// Per-step accent colors — cycled by step index (white font on all).
const STEP_COLORS = ['#D97706', '#0D9488', '#7C3AED', '#0B8A3F', '#B45309', '#334155'];

/** Sets a cell to a literal text value. numberFormat '@' is queued BEFORE
 *  the value so strings starting with "=" (the formula itself, step
 *  expressions) are stored as text instead of being evaluated. */
function setText(range: Excel.Range, value: string): void {
  range.numberFormat = [['@']];
  range.values = [[value]];
}

/**
 * Creates a new worksheet named "Formula Explained" (or "... 2", "... 3"
 * on collision) and paints the explanation. Returns the sheet name.
 * Throws a clean Error on any Office.js failure so the modal can toast it.
 */
export async function writeFormulaExplanation(exp: FormulaExplanation): Promise<string> {
  if (!isInOffice() || typeof Excel === 'undefined') {
    throw new Error('Could not create the explanation sheet');
  }

  try {
    return await Excel.run(async (ctx) => {
      // --- Sync 1: existing sheet names → pick a collision-free name.
      const sheets = ctx.workbook.worksheets;
      sheets.load('items/name');
      await ctx.sync();

      const taken = new Set(sheets.items.map((s) => s.name));
      let name = SHEET_BASE_NAME;
      for (let n = 2; taken.has(name); n++) {
        name = `${SHEET_BASE_NAME} ${n}`;
      }

      const sheet = sheets.add(name);

      // --- Title bar: B2:F2 merged, brand blue.
      const titleRange = sheet.getRange('B2:F2');
      titleRange.merge(true);
      const titleCell = sheet.getRange('B2');
      setText(titleCell, exp.title);
      titleRange.format.fill.color = '#1F6FEB';
      titleRange.format.font.color = '#FFFFFF';
      titleRange.format.font.bold = true;
      titleRange.format.font.size = 14;
      titleRange.format.horizontalAlignment = 'Center';
      titleRange.format.verticalAlignment = 'Center';

      // --- Formula bar: B3:F3 merged, dark slate + monospace.
      const formulaRange = sheet.getRange('B3:F3');
      formulaRange.merge(true);
      const formulaCell = sheet.getRange('B3');
      setText(formulaCell, exp.formula);
      formulaRange.format.fill.color = '#0F172A';
      formulaRange.format.font.color = '#E2E8F0';
      formulaRange.format.font.name = 'Consolas';
      formulaRange.format.horizontalAlignment = 'Center';
      formulaRange.format.verticalAlignment = 'Center';

      const hasSteps = exp.steps && exp.steps.length > 0;
      // Row cursor for whatever comes after the ladder (or directly after
      // the formula bar in fallback mode).
      let nextRow = 5;

      if (hasSteps) {
        // --- Ladder header row B5:E5.
        const headerRange = sheet.getRange('B5:E5');
        headerRange.numberFormat = [[
          '@', '@', '@', '@',
        ]];
        headerRange.values = [['STEP', 'WHAT EXCEL DOES', 'VALUE', 'MEANING']];
        headerRange.format.fill.color = '#F1F5F9';
        headerRange.format.font.bold = true;

        // --- One row per step.
        exp.steps.forEach((step, i) => {
          const row = 6 + i;

          const stepCell = sheet.getRange(`B${row}`);
          setText(stepCell, `STEP ${i + 1}`);
          stepCell.format.fill.color = STEP_COLORS[i % STEP_COLORS.length];
          stepCell.format.font.color = '#FFFFFF';
          stepCell.format.font.bold = true;

          const exprCell = sheet.getRange(`C${row}`);
          setText(exprCell, step.expr);
          exprCell.format.font.name = 'Consolas';
          exprCell.format.fill.color = '#F8FAFC';
          exprCell.format.wrapText = true; // long sub-expressions must not spill

          const valueCell = sheet.getRange(`D${row}`);
          setText(valueCell, step.value);
          valueCell.format.font.bold = true;
          valueCell.format.horizontalAlignment = 'Center';
          valueCell.format.fill.color = '#FFF3CD';
          valueCell.format.wrapText = true;

          const explainCell = sheet.getRange(`E${row}`);
          setText(explainCell, step.explain);
          explainCell.format.wrapText = true; // meaning text wraps inside its column
          explainCell.format.verticalAlignment = 'Top';
        });

        // --- Thin borders across the ladder table (header + steps).
        const ladderRange = sheet.getRange(`B5:E${5 + exp.steps.length}`);
        const edges: Excel.BorderIndex[] = [
          'EdgeTop', 'EdgeBottom', 'EdgeLeft', 'EdgeRight',
          'InsideHorizontal', 'InsideVertical',
        ] as Excel.BorderIndex[];
        for (const edge of edges) {
          const b = ladderRange.format.borders.getItem(edge);
          b.style = 'Continuous' as Excel.BorderLineStyle;
          b.weight = 'Thin' as Excel.BorderWeight;
          b.color = '#CBD5E1';
        }

        // --- RESULT row.
        const resultRow = 6 + exp.steps.length;
        const resultLabel = sheet.getRange(`B${resultRow}`);
        setText(resultLabel, 'RESULT');
        resultLabel.format.fill.color = '#0B8A3F';
        resultLabel.format.font.color = '#FFFFFF';
        resultLabel.format.font.bold = true;

        const resultValue = sheet.getRange(`D${resultRow}`);
        setText(resultValue, exp.result);
        resultValue.format.fill.color = '#CFE2FF';
        resultValue.format.font.bold = true;
        resultValue.format.horizontalAlignment = 'Center';
        for (const edge of ['EdgeTop', 'EdgeBottom', 'EdgeLeft', 'EdgeRight'] as Excel.BorderIndex[]) {
          const b = resultValue.format.borders.getItem(edge);
          b.style = 'Continuous' as Excel.BorderLineStyle;
          b.weight = 'Thick' as Excel.BorderWeight;
          b.color = '#1F6FEB';
        }

        nextRow = resultRow + 2;
      }

      // --- Summary block: "STEP BY STEP" header, then plain text lines.
      // Each line is merged across B..F with wrap so long sentences use the
      // full card width instead of overlapping into unformatted columns.
      if (exp.summary && exp.summary.length > 0) {
        const summaryHeader = sheet.getRange(`B${nextRow}`);
        setText(summaryHeader, 'STEP BY STEP');
        summaryHeader.format.font.bold = true;
        exp.summary.forEach((line, i) => {
          const row = nextRow + 1 + i;
          const lineRange = sheet.getRange(`B${row}:F${row}`);
          lineRange.merge(true);
          const cell = sheet.getRange(`B${row}`);
          setText(cell, line);
          lineRange.format.wrapText = true;
          lineRange.format.verticalAlignment = 'Top';
          lineRange.format.horizontalAlignment = 'Left';
        });
      }

      // --- Column widths.
      sheet.getRange('B:B').format.columnWidth = 90;
      sheet.getRange('C:C').format.columnWidth = 260;
      sheet.getRange('D:D').format.columnWidth = 120;
      sheet.getRange('E:E').format.columnWidth = 320;

      // --- Row heights: wrapped text needs taller rows — autofit everything
      // written so nothing clips or overlaps.
      sheet.getUsedRange().format.autofitRows();

      sheet.activate();

      // --- Sync 2: flush every queued write in one round-trip.
      await ctx.sync();
      return name;
    });
  } catch (err) {
    console.error('[formulaSheet] writeFormulaExplanation failed:', err);
    throw new Error('Could not create the explanation sheet');
  }
}
